import { TelegramHaptic, useTelegram } from '@/components/telegram';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SFIcon } from '@/components/ui/sf-icon';
import { TELEGRAM_CONFIG } from '@/lib/telegramConfig';
import { cn } from '@/lib/utils';
import { sfIcons } from '@/lib/sf-icons';
import { semanticTokens } from '@packages/config/theme';
import { MessageCircle } from 'lucide-react';

interface DashboardTelegramCardProps {
  onOpenSettings?: () => void;
}

export const DashboardTelegramCard = ({ onOpenSettings }: DashboardTelegramCardProps) => {
  const {
    user,
    isInTelegram,
    isAuthorized,
    customerId,
    hapticFeedback
  } = useTelegram();

  if (!isInTelegram && !isAuthorized) {
    return (
      <Card className={cn("shadow-card hover:shadow-card-hover transition-shadow duration-240")}>
        <CardHeader>
          <CardTitle className={cn(semanticTokens.typography.h3, "flex items-center gap-2")}>
            <MessageCircle className="h-5 w-5 text-muted-foreground" />
            Connect Telegram
          </CardTitle>
          <CardDescription className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
            Use Supabank directly in Telegram for better experience
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <span className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>Status</span>
            <Badge variant="secondary" className={cn(semanticTokens.warning.bg, semanticTokens.warning.fg, semanticTokens.warning.border)}>
              <SFIcon icon={sfIcons.warning} className="h-3 w-3 mr-1" aria-hidden="true" />
              Not Connected
            </Badge>
          </div>

          <div className="pt-2 border-t">
            <TelegramHaptic type="selection">
              <Button
                onClick={() => {
                  hapticFeedback.selection();
                  window.open(TELEGRAM_CONFIG.MINI_APP_URL, '_blank');
                }}
                className="w-full"
                variant="default"
              >
                <SFIcon icon={sfIcons.externalLink} className="h-4 w-4 mr-2" />
                Launch in Telegram
              </Button>
            </TelegramHaptic>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("shadow-card hover:shadow-card-hover transition-shadow duration-240")}>
      <CardHeader>
        <CardTitle className={cn(semanticTokens.typography.h3, "flex items-center gap-2")}>
          <MessageCircle className="h-5 w-5 text-primary" />
          Telegram Integration
        </CardTitle>
        <CardDescription className={cn(semanticTokens.typography.body, semanticTokens.fg.muted)}>
          Your account is connected to Telegram
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <span className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>Status</span>
          <Badge variant="outline" className="bg-success/5 text-success border-success/20">
            <SFIcon icon={sfIcons.success} className="h-3 w-3 mr-1" />
            Connected
          </Badge>
        </div>

        {user && (
          <div className="flex items-center justify-between">
            <span className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>Account</span>
            <div className="text-right">
              <p className={cn(semanticTokens.typography.label)}>{user.first_name}</p>
              {user.username && (
                <p className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>@{user.username}</p>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>Environment</span>
          <Badge variant="outline" className={isInTelegram ? "bg-primary/5 text-primary border-primary/20" : cn(semanticTokens.bg.muted, semanticTokens.fg.default, semanticTokens.border.subtle)}>
            {isInTelegram ? 'Telegram App' : 'Web Browser'}
          </Badge>
        </div>

        {customerId && (
          <div className="flex items-center justify-between">
            <span className={cn(semanticTokens.typography.caption, semanticTokens.fg.muted)}>Customer ID</span>
            <code className={cn(semanticTokens.typography.caption, "bg-muted px-2 py-1 rounded")}>
              {customerId.slice(0, 8)}...
            </code>
          </div>
        )}

        <div className="pt-2 border-t">
          <TelegramHaptic type="selection">
            <Button
              onClick={() => {
                hapticFeedback.selection();
                onOpenSettings?.();
              }}
              variant="outline"
              className="w-full"
            >
              <SFIcon icon={sfIcons.settings} className="h-4 w-4 mr-2" />
              Telegram Settings
            </Button>
          </TelegramHaptic>
        </div>
      </CardContent>
    </Card>
  );
};